import { App, Avatar, Button, Card, DatePicker, Form, Input, Select, Spin, Switch, Tag, Typography } from 'antd';
import { LockOutlined, LogoutOutlined, SearchOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '@stores/auth.store';
import { useTeachers } from '@hooks/tenant/useTeachers';
import { subscribeAttendanceMonthEntries, upsertAttendanceEntry } from '@services/registry';
import { signInWithPortal } from '@services/firebase/sessionService';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const KioskShell = styled.div`
    height: 100vh;
    max-height: 100vh;
    background: ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#0f172a' : '#f8fafc')};
    color: ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#f8fafc' : '#0f172a')};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
`;

const KioskHeader = styled.header`
    position: sticky;
    top: 0;
    z-index: 100;
    background: ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#1e293b' : '#ffffff')};
    border-bottom: 1px solid ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#334155' : '#e2e8f0')};
    padding: 12px 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const HeaderTopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const BrandBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const BrandLogo = styled.img`
    width: 32px;
    height: 32px;
    object-fit: contain;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DateSelectorCard = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    background: ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#0f172a' : '#f1f5f9')};
    padding: 2px 8px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#334155' : '#cbd5e1')};
`;

const SubHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
`;

const StatsPill = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
`;

const MainContent = styled.main`
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    padding: 16px;
    flex: 1;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 12px;
`;

const TeacherCard = styled.div`
    background: ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#1e293b' : '#ffffff')};
    border: 1px solid ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#334155' : '#e2e8f0')};
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
    }
`;

const TeacherInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
`;

const TeacherMeta = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const TeacherName = styled.div`
    font-weight: 700;
    font-size: 14px;
    color: ${({ theme }) => (theme.colors?.background?.default === '#141414' ? '#f8fafc' : '#0f172a')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TeacherEmail = styled.div`
    font-size: 11px;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SwitchWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
`;

const LoginShell = styled.div`
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0f172a;
    padding: 20px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
`;

const LoginCard = styled(Card)`
    width: 100%;
    max-width: 380px;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    border: 1px solid #334155;
    background: #1e293b;

    .ant-card-body {
        padding: 28px 24px;
    }
`;

export default function TeacherAttendanceKiosk() {
    const { message } = App.useApp();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const setSession = useAuthStore((s) => s.setSession);
    const logout = useAuthStore((s) => s.logout);
    const tenantId = user?.tenantId;

    const [selectedDate, setSelectedDate] = useState(() => dayjs());
    const [searchText, setSearchText] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [entries, setEntries] = useState([]);
    const [savingKey, setSavingKey] = useState(null);
    const [loginForm] = Form.useForm();

    const dateKey = selectedDate.format('YYYY-MM-DD');
    const monthKey = selectedDate.format('YYYY-MM');

    const { rows: teachers, loading: teachersLoading } = useTeachers({
        tenantId,
        pageSize: 200,
        includeRows: !!tenantId,
    });

    useEffect(() => {
        if (!tenantId || !monthKey) return;
        return subscribeAttendanceMonthEntries({
            tenantId,
            scope: 'teachers',
            monthKey,
            onNext: setEntries,
            onError: (err) => message.error(err?.message || 'Attendance sync error'),
        });
    }, [tenantId, monthKey, message]);

    const entryMap = useMemo(() => {
        const map = new Map();
        for (const e of entries) {
            if (!e?.dateKey || !e?.personId) continue;
            map.set(`${e.dateKey}_${e.personId}`, e);
        }
        return map;
    }, [entries]);

    const handleLoginSubmit = async (values) => {
        setLoginLoading(true);
        try {
            const { user: authedUser } = await signInWithPortal(values.email, values.password, 'tenant');
            setSession({ user: authedUser, role: 'tenant', permissions: authedUser.permissions || ['*'] });
            message.success('Signed in to Attendance Terminal');
        } catch (err) {
            message.error(err?.message || 'Login failed. Check your credentials.');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleToggleStatus = async (personId, currentStatus) => {
        if (!tenantId) return;
        const nextStatus = currentStatus === 'P' ? 'A' : 'P';
        const key = `${dateKey}_${personId}`;
        setSavingKey(key);
        try {
            await upsertAttendanceEntry({
                tenantId,
                scope: 'teachers',
                monthKey,
                dateKey,
                personId,
                status: nextStatus,
            });
        } catch {
            message.error('Failed to update attendance');
        } finally {
            setSavingKey(null);
        }
    };

    const filteredTeachers = useMemo(() => {
        if (!Array.isArray(teachers)) return [];
        if (!searchText) return teachers;
        const query = searchText.toLowerCase();
        return teachers.filter((t) => {
            const name = (t?.displayName || `${t?.firstName || ''} ${t?.lastName || ''}`).toLowerCase();
            const email = (t?.email || '').toLowerCase();
            return name.includes(query) || email.includes(query);
        });
    }, [teachers, searchText]);

    if (!user || !tenantId) {
        return (
            <LoginShell>
                <LoginCard>
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <img src="/edulink-mark.png" alt="EDULINK" style={{ width: 44, height: 44, marginBottom: 8 }} />
                        <Title level={4} style={{ color: '#fff', margin: 0 }}>Attendance Terminal</Title>
                        <Text style={{ color: '#94a3b8', fontSize: 12 }}>Sign in with school credentials</Text>
                    </div>

                    <Form form={loginForm} layout="vertical" onFinish={handleLoginSubmit}>
                        <Form.Item
                            name="email"
                            rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Valid email required' }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                                placeholder="School Email"
                                size="large"
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: 'Please enter password' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                                placeholder="Password"
                                size="large"
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>

                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            loading={loginLoading}
                            block
                            style={{ borderRadius: 8, height: 42, fontWeight: 700, marginTop: 4, background: '#0284c7' }}
                        >
                            Sign In
                        </Button>
                    </Form>
                </LoginCard>
            </LoginShell>
        );
    }

    return (
        <KioskShell>
            <KioskHeader>
                <HeaderTopRow>
                    <BrandBlock>
                        <BrandLogo src={user?.logoUrl || "/edulink-mark.png"} alt="EDULINK" />
                        <div>
                            <Title level={5} style={{ margin: 0, lineHeight: 1.2 }}>
                                {user?.schoolName || 'Teacher Attendance'}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Daily Attendance Terminal
                            </Text>
                        </div>
                    </BrandBlock>

                    <HeaderActions>
                        <DateSelectorCard>
                            <CalendarOutlined style={{ color: '#0284c7', fontSize: 14 }} />
                            <DatePicker
                                value={selectedDate}
                                onChange={(d) => d && setSelectedDate(d)}
                                format="DD MMM YYYY"
                                allowClear={false}
                                style={{ border: 'none', background: 'transparent', width: 120, fontSize: 13 }}
                            />
                            <Button
                                size="small"
                                type={selectedDate.isSame(dayjs(), 'day') ? 'primary' : 'default'}
                                onClick={() => setSelectedDate(dayjs())}
                                style={{ borderRadius: 6, fontSize: 11, paddingInline: 8 }}
                            >
                                Today
                            </Button>
                        </DateSelectorCard>

                        <Button
                            icon={<LogoutOutlined />}
                            size="small"
                            onClick={() => {
                                logout();
                                navigate('/tenant/sign-in');
                            }}
                            title="Sign out"
                            style={{ borderRadius: 6 }}
                        />
                    </HeaderActions>
                </HeaderTopRow>

                <SubHeaderRow>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Search teacher name or email..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        style={{ width: '100%', borderRadius: 8 }}
                    />
                </SubHeaderRow>
            </KioskHeader>

            <MainContent>
                {teachersLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : filteredTeachers.length === 0 ? (
                    <Card style={{ textAlign: 'center', padding: '30px 0', borderRadius: 12 }}>
                        <Text type="secondary">No teachers found</Text>
                    </Card>
                ) : (
                    <CardsGrid>
                        {filteredTeachers.map((t, idx) => {
                            const pid = t.id || t.authUid;
                            const name = t?.displayName || `${t?.firstName || ''} ${t?.lastName || ''}`.trim() || '—';
                            const email = t?.email || t?.userEmail || '';

                            const entry = entryMap.get(`${dateKey}_${pid}`);
                            const status = entry?.status || 'A';
                            const isPresent = status === 'P';
                            const busy = savingKey === `${dateKey}_${pid}`;

                            return (
                                <TeacherCard key={pid || idx}>
                                    <TeacherInfo>
                                        <Avatar
                                            size={38}
                                            style={{
                                                backgroundColor: '#334155',
                                                fontWeight: 800,
                                                fontSize: 15,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {name[0]?.toUpperCase()}
                                        </Avatar>

                                        <TeacherMeta>
                                            <TeacherName title={name}>{name}</TeacherName>
                                            <TeacherEmail title={email}>{email || 'No email'}</TeacherEmail>
                                        </TeacherMeta>
                                    </TeacherInfo>

                                    <SwitchWrap>
                                        {busy ? (
                                            <Spin size="small" />
                                        ) : (
                                            <Switch
                                                checked={isPresent}
                                                onChange={() => handleToggleStatus(pid, status)}
                                                checkedChildren="P"
                                                unCheckedChildren="A"
                                                style={{
                                                    backgroundColor: isPresent ? '#16a34a' : '#dc2626',
                                                    transform: 'scale(1.1)',
                                                }}
                                            />
                                        )}
                                        <Text style={{ fontSize: 10, fontWeight: 800, color: isPresent ? '#16a34a' : '#dc2626' }}>
                                            {isPresent ? 'PRESENT' : 'ABSENT'}
                                        </Text>
                                    </SwitchWrap>
                                </TeacherCard>
                            );
                        })}
                    </CardsGrid>
                )}
            </MainContent>
        </KioskShell>
    );
}
